import {
  GraphQLObjectType,
  GraphQLNonNull,
  GraphQLID,
  GraphQLString,
  GraphQLList,
  GraphQLBoolean,
  GraphQLInt,
  GraphQLEnumType
} from 'graphql'
import { ServerEnvironment } from '../../../components/servers-grid/shared'
import { serverEnvironmentType } from './server'
import { dateTimeType } from './date-time-type'

export type RouteStatus = 'DISCOVERED' | 'EXERCISED' | 'EXCLUDED'

const routeStatusType = new GraphQLEnumType({
  name: 'RouteStatus',
  description: 'Statuses for a an application route',
  values: {
    DISCOVERED: {
      description: 'A route that is discovered but has not been exercised'
    },
    EXERCISED: {
      description: 'A route that has been exercised'
    },
    EXCLUDED: {
      description:
        'A route that has been excluded from route coverage metrics calculations'
    }
  }
})

export interface RouteServer {
  readonly id: string
  readonly name: string
  readonly path: string
  readonly environment: ServerEnvironment
  readonly enabled: boolean
  readonly agentVersion: string
  // support simpler response for servers/ api
  readonly server_id?: string
}

export const routeServerType = new GraphQLObjectType({
  name: 'RouteServer',
  fields: () => ({
    id: {
      description: 'The unique ID for the server',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Human readable name for the server',
      type: new GraphQLNonNull(GraphQLString)
    },
    path: {
      description: 'The installation path of the server',
      type: new GraphQLNonNull(GraphQLString)
    },
    environment: {
      description: 'Server environment',
      type: new GraphQLNonNull(serverEnvironmentType)
    },
    enabled: {
      description: 'Whether the server is enabled',
      type: GraphQLBoolean
    },
    agentVersion: {
      description: 'The agent version running on the server',
      type: new GraphQLNonNull(GraphQLString)
    }
  })
})

export interface RouteApplication {
  readonly id: string
  readonly name: string
}

export const routeApplicationType = new GraphQLObjectType({
  name: 'RouteApplication',
  fields: () => ({
    id: {
      description: 'The unique ID of the application',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Name of application',
      type: new GraphQLNonNull(GraphQLString)
    }
  })
})

export interface Route {
  readonly id: string
  readonly signature: string
  readonly hash: string
  readonly servers: RouteServer[]
  readonly vulnerabilityCount: number
  readonly criticalVulnerabilityCount: number
  readonly exercised?: Date
  readonly discovered?: Date
  readonly status: RouteStatus
  // below only used in route-coverage-grid currently
  readonly serversTotal?: number
  readonly environments?: string[]
  readonly app: RouteApplication
}

export const routeType = new GraphQLObjectType({
  name: 'Route',
  fields: () => ({
    id: {
      description: "The route's cache ID",
      type: new GraphQLNonNull(GraphQLID)
    },
    signature: {
      description: "The route's signature",
      type: new GraphQLNonNull(GraphQLString)
    },
    hash: {
      description: "The route's hash",
      type: new GraphQLNonNull(GraphQLString)
    },
    servers: {
      description: 'A list of servers that the route is on',
      type: new GraphQLNonNull(new GraphQLList(routeServerType))
    },
    serversTotal: {
      description: 'The total number of servers for this route',
      type: GraphQLInt
    },
    vulnerabilityCount: {
      description: 'The number of vulnerabilities for the route',
      type: GraphQLInt
    },
    criticalVulnerabilityCount: {
      description: 'The number of critical vulnerabilities for the route',
      type: GraphQLInt
    },
    discovered: {
      description: 'The timestamp of the first time the route was discovered',
      type: dateTimeType
    },
    environments: {
      description: 'The distinct set of environments for this route',
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    },
    exercised: {
      description: 'The timestamp of the last time the route was exercised',
      type: dateTimeType
    },
    status: {
      description: 'The status of the route',
      type: routeStatusType
    },
    app: {
      description: 'The application that is associated with the route',
      type: routeApplicationType
    }
  })
})
